import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../../../../api/AuthService";
import { useAlert } from "../../../../../context/AlertContext";
import LoaderHelper from "../../../../../utils/Loader/LoaderHelper";
import EditInvoicePage from "../../../../../entities/DashboardPage/AccountingModule/Invoice/EditInvoice";

const EditInvoice = () => {
  const { showAlert } = useAlert();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [template, setTemplate] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productInformation, setProductInformation] = useState([]);

  // Derived/calculated values
  const [total, setTotal] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);
  const [taxValue, setTaxValue] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const [templateRes, customerRes, productRes] = await Promise.all([
        AuthService.getInvoiceDetail(state?.item?._id),
        AuthService.getCustomer(),
        AuthService.getItemsList(),
      ]);

      if (templateRes?.success) {
        setTemplate(templateRes.data);
        setProductInformation(templateRes.data.productInformation || []);
        calculateTotals(
          templateRes.data.productInformation,
          templateRes.data.discountPercentage,
          templateRes.data.taxPercentage
        );
      }

      if (customerRes?.success) {
        setCustomers(customerRes.data.customers);
      }

      if (productRes?.success) {
        setProducts(productRes.data.items);
      }

      LoaderHelper.loaderStatus(false);
    } catch (error) {
      showAlert("Failed to load data", "error");
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleFieldChange = (field, value) => {
    if ((field === "quantity" || field === "unit") && Number(value) < 0) {
      return;
    }
    setTemplate((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "discountPercentage" || field === "taxPercentage") {
        calculateTotals(
          productInformation,
          updated.discountPercentage,
          updated.taxPercentage
        );
      }
      return updated;
    });
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...productInformation];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value,
    };

    // If the productId changes, populate unitCost
    if (field === "productId") {
      const selectedProduct = products.find(
        (p) => p._id === value || p.productId === value
      );
      if (selectedProduct) {
        updatedProducts[index].unitCost = selectedProduct.sellingPrice || 0;
        updatedProducts[index].name = selectedProduct.title;
      }
    }

    // Always recalculate amount if unitCost or quantity changed
    const qty = +updatedProducts[index].quantity || 0;
    const cost = +updatedProducts[index].unitCost || 0;
    updatedProducts[index].amount = qty * cost;

    setProductInformation(updatedProducts);
    calculateTotals(
      updatedProducts,
      template?.discountPercentage,
      template?.taxPercentage
    );
  };

  const handleAddProduct = () => {
    setProductInformation((prev) => [
      ...prev,
      {
        name: "",
        description: "",
        unitCost: 0,
        quantity: 0,
        amount: 0,
        productId: "",
      },
    ]);
  };

  const handleRemoveProduct = (index) => {
    const updated = [...productInformation];
    updated.splice(index, 1);
    setProductInformation(updated);
    calculateTotals(
      updated,
      template.discountPercentage,
      template.taxPercentage
    );
  };

  const calculateTotals = (productsArr, discountPerc, taxPerc) => {
    const total = productsArr.reduce((sum, p) => sum + (+p.amount || 0), 0);
    const discount = (total * (+discountPerc || 0)) / 100;
    const tax = ((total - discount) * (+taxPerc || 0)) / 100;
    const grand = total - discount + tax;

    setTotal(total);
    setDiscountValue(discount);
    setTaxValue(tax);
    setGrandTotal(grand);
  };

  const validateForm = () => {
    if (!template?.customer?._id) return "Customer is required";

    for (const product of productInformation) {
      if (!product.name || +product.quantity <= 0 || +product.unitCost <= 0) {
        return "Each product must have a name, and valid quantity and cost";
      }
    }
    return null;
  };

  const handleUpdateInvoice = async () => {
    const validationError = validateForm();
    if (validationError) {
      showAlert(validationError, "error");
      return;
    }
    const updatedData = {
      ...template,
      productInformation,
      total,
      discountValue,
      taxValue,
      grandTotal,
      customer: template.customer._id
    };

    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.updateInvoice(
        template._id,
        updatedData
      );
      if (result?.success) {
        showAlert("Invoice updated successfully", "success");
        navigate("/dashboard/invoice")
      } else {
        showAlert(result?.message, "error");
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      showAlert("Server error while updating", "error");
      LoaderHelper.loaderStatus(false);
    }
  };

  const isFormValid = () => {
    if (!template) return false;

    const {
      customer,
      billingAddress,
      shippingAddress,
      paymentTerms,
      invoiceDate,
      dueDate,
      dailyPenaltyAmount,
      discountPercentage,
      taxPercentage,
    } = template;

    // Check all basic fields
    if (
      !customer ||
      !paymentTerms ||
      !invoiceDate ||
      !dueDate ||
      dailyPenaltyAmount === undefined ||
      discountPercentage === undefined ||
      taxPercentage === undefined
    ) {
      return false;
    }

    // Check billing address
    const requiredBillingFields = [
      "country",
      "state",
      "city",
      "pinCode",
      "street1",
      "phone",
    ];
    for (const field of requiredBillingFields) {
      if (!billingAddress[field]) return false;
    }

    // Check shipping address
    const requiredShippingFields = [
      "country",
      "state",
      "city",
      "pinCode",
      "street1",
      "phone",
    ];
    for (const field of requiredShippingFields) {
      if (!shippingAddress[field]) return false;
    }

    // Check product items
    for (const item of productInformation) {
      if (
        !item.productId ||
        !item.name ||
        !item.description ||
        item.unitCost === undefined ||
        item.quantity === undefined
      ) {
        return false;
      }
    }
    return true;
  };

  return (
    <EditInvoicePage
      template={template}
      setTemplate={setTemplate}
      customers={customers}
      loading={loading}
      products={products}
      productInformation={productInformation}
      handleFieldChange={handleFieldChange}
      handleProductChange={handleProductChange}
      handleAddProduct={handleAddProduct}
      handleRemoveProduct={handleRemoveProduct}
      handleUpdateInvoice={handleUpdateInvoice}
      total={total}
      discountValue={discountValue}
      taxValue={taxValue}
      grandTotal={grandTotal}
      isFormValid={isFormValid}
    />
  );
};

export default EditInvoice;
